import escapeRegExp from 'lodash/escapeRegExp';
import isNil from 'lodash/isNil';

declare let window;

/**
 * Take a string to match against and expand it to a regex pattern matching
 * uppercase and lowercase for each letter.
 * "Hello-There!" => "[Hh][Ee][Ll][Ll][Oo]-[Tt][Hh][Ee][Rr][Ee]!"
 */
export function caseInsensitiveRegExp(input: string): string {
  return escapeRegExp(input).split('').map(chr => {
    const code = chr.charCodeAt(0);
    if (code >= 65 && code <= 90) {
      return `[${chr}${chr.toLowerCase()}]`;
    }
    if (code >= 97 && code <= 122) {
      return `[${chr.toUpperCase()}${chr}]`;
    }
    return chr;
  }).join('');
}

/**
 * Native implimentation of angularjs' $interpolate function,
 * replaces '{{variable}}' in string with values from the replace map
 * @param {string} input - string to perform interpolations on
 * @param {Object<[key: string]: string>} - key map of variables to replace
 */
export function interpolate(input: string, params: {[key: string]: string}): string {
  return input.replace(/{{([^}]+)}}/g, (val, key) => params[key] || '');
}

/**
 * Returns boolean whether or not input is a 24 character hex value
 * @param {string} input - value to test
 * @return {boolean} - true if input matches 24 character hex value, else false
 */
export function isContainerId(input: string): boolean {
  return /^[0-9a-fA-F]{24}$/.test(input);
}

/**
 * Give any value returns a 'display' string for that value, notably '-' for nil values or empty strings
 * @param {any} value - any value to get display string for
 * @returns {string} - value for display
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function displayValue(value: any): string {
  if (isNil(value) || value === '') {
    return '-';
  }
  if (typeof value === 'object') {
    return JSON.stringify(value);
  }
  return String(value);
}

const elementIdBounds = [parseInt('100000000', 36), parseInt('zzzzzzzzz', 36)];
/**
 * @returns 9-character base 36 ([0-9a-z]) unique ID
 */
export function generateElementId(): string {
  return Math.floor(elementIdBounds[0] + Math.random() * (elementIdBounds[1] - elementIdBounds[0])).toString(36);
}

// https://developer.mozilla.org/en-US/docs/Web/API/SubtleCrypto/digest#Converting_a_digest_to_a_hex_string
type HashAlgorithm = 'SHA-1' | 'SHA-256' | 'SHA-384' | 'SHA-512';
type HashFormat = 'hex' | 'dec';
export async function hash(message: string, format: HashFormat = 'hex', algorithm: HashAlgorithm = 'SHA-256'): Promise<string> {
  const msgUint8 = new TextEncoder().encode(message);
  const crypto = typeof window === 'undefined' ? self.crypto : window.crypto;
  const hashBuffer = await crypto.subtle.digest(algorithm, msgUint8);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  if (format === 'dec') {
    return hashArray.join('');
  }
  return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
}

export enum GroupingCharacterType {
  Parentheses = 'parenthesis',
  Brackets = 'brackets',
  Braces = 'braces',
  Chevrons = 'chevrons',
}
export function getTopLevelGroupings(str: string, groupingType: GroupingCharacterType): string[] {
  let left;
  let right;
  switch (groupingType) {
    case GroupingCharacterType.Parentheses:
      left = '(';
      right = ')';
      break;
    case GroupingCharacterType.Brackets:
      left = '[';
      right = ']';
      break;
    case GroupingCharacterType.Braces:
      left = '{';
      right = '}';
      break;
    case GroupingCharacterType.Chevrons:
      left = '<';
      right = '>';
      break;
    default:
      left = '(';
      right = ')';
  }
  const groups = [];
  let count = 0;
  let pos = 0;
  const strSplit = str.split('');
  for (let i = 0, len = strSplit.length; i < len; i++) {
    const character = strSplit[i];
    if (character === left) {
      count++;
      if (count === 1) {
        pos = i + 1;
      }
      continue;
    }
    if (character === right) {
      count--;
      if (count === 0) {
        groups.push(str.substr(pos, i - pos));
      }
      if (count < 0) {
        throw new Error(`Found malformed string: missing '${left}'.`);
      }
    }
  }
  if (count > 0) {
    throw new Error(`Found malformed string: missing '${right}'.`);
  }
  return groups.length > 0 ? groups : [str];
}

/**
 * Takes a string and returns initials based on either the first two letters of
 * one name or the first letters or two names or returns an empty string
 * @param name string
 * @returns two character string for initials
 */
export function getInitials(name: string): string {
  const names = name.split(' ');
  if (names.length > 1) {
    return names[0].substring(0,1) + names[1].substring(0,1);
  }
  return names[0].substring(0,2);
}

/**
 * Wraps string in double quotes if string contains a space and is not already quoted
 * @param {string} value - The string to be quoted
 * @returns {string} - String wrapped in double quotes
 */
export function quoteString(value: string): string {
  return value.match(/^[^"].*\s.*[^"]$/) ? `"${value}"` : value;
}

/**
 * Converts first letter of works to upper case
 * @param {string} value - The string to convert to title case
 * @returns {string} - String with all words capitalized
 */
export function toTitleCase(value: string): string {
  return value.replace(/\b\S/g, c => c.toUpperCase());
}
